import * as React from "react";
import { InfoIcon } from "../../../../icons/Info";
import styles from "./ChangeBookingDetails.module.scss";
import { ClinicianServiceSelection } from "../../../../../../store/department";
import {
    IClient,
    IClientBooking,
    IClinician,
    ServiceDeliveryOption,
} from "intake_shared/src/types";
import { Badge } from "../../../../bits/Badge/Badge";
import { getV2BadgePropsByPractice } from "../../../../../../constants";
import store from "../../../../../../store";
import { DatePicker } from "../../../../bits/DatePicker/DatePicker";
import moment from "../../../../../../utils/app_moment";
import { FormCheckbox } from "../../../../bits/FormCheckbox/FormCheckbox";
import {
    findLabelByPracticeId,
    getNowEquivalent,
    getStringFromScheduleDate,
    toTitleCase,
} from "../../../../../../utils/utils";
import { CacheContext } from "../../../../../../contexts/CacheContext";
import { getIntakeEmailSignature } from "../../../../../../utils/intakeEmailSignature";
import { useToasts } from "react-toast-notifications";
import { AclContext } from "../../../../../../contexts/AclContext";
import { Select } from "../../../../bits/Select/Select";
import { TextArea } from "../../../../bits/TextArea/TextArea";
import { Dialog } from "../../../../bits/Dialog/Dialog";
import Alert from "../../../../bits/Alert/Alert";
import { sendAppointmentHeldEmails } from "../../../../../../utils/queue_email";
import { TUpdateClient } from "../../../clientUiTypes";
import { getHeldAppointmentCheck } from "../../../../../../utils/consent_form";
import { safeToDate } from "../../../../../v2utils";

interface ChangeBookingDetailsProps {
    client: IClient;
    updateClient: TUpdateClient;
    booking: IClientBooking;
    appointmentState: "held" | "confirmed";
    clinician?: IClinician[];
    viewFrom: "appt_held" | "appt_confirmed";
    showEditModal: boolean;
    setShowEditModal: React.Dispatch<React.SetStateAction<boolean>>;
}

type Option = {
    label: string;
    value: string;
};

export default function ChangeBookingDetails({
    client,
    updateClient,
    booking,
    appointmentState,
    clinician,
    viewFrom,
    showEditModal,
    setShowEditModal,
}: ChangeBookingDetailsProps) {
    const olc = React.useContext(CacheContext);
    const allDepartmentsAndServices = olc?.allDepartmentAndServices || {};
    const loggedInUserEmail = React.useContext(AclContext)?.authUser.user?.email;
    const requestedServices = olc?.requestedServices || {};
    const { addToast } = useToasts();
    const [practiceId, setPracticeId] = React.useState("");
    const [clinicianServiceSelections, setClinicianServiceSelections] =
        React.useState<ClinicianServiceSelection>({});
    const emailSignature = getIntakeEmailSignature(olc);
    const [selectedPractice, setSelectedPractice] = React.useState(
        findLabelByPracticeId(booking.practice_id, allDepartmentsAndServices) || ""
    );

    const [startingDate, setStartingDate] = React.useState<Date>(
        moment(booking.starting_date).toDate()
    );
    const [requestConsultCall, setRequestConsultCall] = React.useState(
        Boolean((client.appointment_checks || {})[booking.id]?.client_requested_call)
    );

    const [consultCallNotes, setConsultCallNotes] = React.useState(
        (client.appointment_checks || {})[booking.id]?.consult_call_notes || ""
    );
    const [serviceDeliveryOption, setServiceDeliveryOption] = React.useState(
        booking.service_delivery_option
    );
    const [location, setLocation] = React.useState(booking.location || "");
    const [office, setOffice] = React.useState(booking.office || "");
    const [requestedService, setRequestedService] = React.useState(
        booking.requested_service
    );

    // Retrieve the services for the requested department
    const departmentServices =
        clinicianServiceSelections[booking?.clinician_id]?.services;

    const emails = [
        "intake@humantold.com",
        "n.paladino@humantold.com",
        "r.rhoad@humantold.com",
        "Ruby@humantold.com",
        "Nancy@huamntold.com",
    ];

    const canSeeForm = emails.includes(loggedInUserEmail as string);

    const departmentCount =
        clinicianServiceSelections[booking.clinician_id]?.departments?.length || 0;

    const clinicianData = clinician?.find((c) => c.id === booking.clinician_id);

    // Find office windows for this clinician that overlap the booking's recurring time slot.
    // Uses the same day-of-week/time normalisation as the initial booking flow.
    const matchingOfficeWindows = React.useMemo(() => {
        const bookingStart = getNowEquivalent(
            moment(getStringFromScheduleDate(booking.start))
        );
        const bookingEnd = getNowEquivalent(
            moment(getStringFromScheduleDate(booking.end))
        );
        return (clinicianData?.office_windows || []).filter((w) => {
            if (!w.location || w.location === "Remote") return false;
            const wStart = getNowEquivalent(
                moment(getStringFromScheduleDate(w.start))
            );
            const wEnd = getNowEquivalent(moment(getStringFromScheduleDate(w.end)));
            return (
                bookingStart.isBetween(wStart, wEnd, "minutes", "[]") &&
                bookingEnd.isBetween(wStart, wEnd, "minutes", "[]")
            );
        });
    }, [clinicianData, booking.start, booking.end]);

    const inPersonOfficeOptions: Option[] = matchingOfficeWindows
        .filter(
            (w, idx, arr) =>
                arr.findIndex(
                    (x) => x.location === w.location && x.office === w.office
                ) === idx
        )
        .map((w) => ({
            label: `${w.location} — ${w.office}`,
            value: `${w.location}||${w.office}`,
        }));

    const serviceDeliveryOptions: Option[] = [
        { label: "In-Person", value: "in_person" },
        { label: "Telemed", value: "remote" },
    ];

    const clinicianServiceOptions: Option[] = (
        clinicianServiceSelections[booking.clinician_id]?.departments || []
    )?.map((dep) => ({
        label: dep?.replaceAll("_", " "),
        value: dep,
    }));

    const generateAvailableDates = () => {
        const today = moment();

        const availableDates: moment.Moment[] = [];

        for (let i = 0; i < 28; i++) {
            const currentDate = today.clone().add(i, "days");

            const isBeforeEndDate = booking.ending_date
                ? moment(booking.ending_date).isAfter(currentDate)
                : true;

            const isNotVacationPeriod = !clinicianData?.unavailable?.some((e) =>
                currentDate.isBetween(moment(e.start), moment(e.end), "day", "[]")
            );

            const isValidDate =
                currentDate.isAfter(today, "days") &&
                isBeforeEndDate &&
                isNotVacationPeriod;

            if (isValidDate) {
                availableDates.push(currentDate);
            }
        }

        return availableDates;
    };

    const availableApptStartingDates = generateAvailableDates();

    const requestedServiceOptions: Option[] =
        departmentServices?.map((s, idx) => ({
            label: toTitleCase(s?.replaceAll("_", " ")),
            value: s,
        })) || [];

    const servicePracticeOptions: Option[] = Object.entries(
        allDepartmentsAndServices
    ).map(([department, details]) => ({
        label: details.label || "",
        value: department,
    }));

    const changeBookingDetails = async () => {
        const effectiveBookingPracticeId =
            practiceId ||
            booking.practice_id ||
            allDepartmentsAndServices[
                clinicianServiceSelections[booking.clinician_id]?.departments[0]
            ]?.practice_id ||
            (process.env.REACT_APP_MANTLE_PRACTICE_ID as string) ||
            "";
        const bookingDetailsUpdate = {
            service_delivery_option: serviceDeliveryOption,
            starting_date: startingDate.toISOString(),
            requested_service:
                requestedServices[requestedService] || booking.requested_service,
            practice_id: effectiveBookingPracticeId,
            location: serviceDeliveryOption === "remote" ? "Remote" : location,
            office: serviceDeliveryOption === "remote" ? "" : office,
        };
        const updated = await store.updateBooking(
            booking.clinician_id,
            booking.client_id,
            booking.id,
            {
                ...bookingDetailsUpdate,
            },
            {
                client_requested_call: requestConsultCall,
                consult_call_notes: consultCallNotes,
            },
            appointmentState,
            loggedInUserEmail!
        );
        if (updated) {
            const bookingField =
                appointmentState === "held" ? "held_bookings" : "confirmed_bookings";
            const updatedBooking = updated[bookingField].find(
                (entry) => entry.id === booking.id
            )!;
            const savedAppointmentCheck = updated.appointment_checks![booking.id];
            const updatedAppointmentChecks = {
                ...client.appointment_checks,
                [booking.id]: {
                    ...getHeldAppointmentCheck(
                        client.appointment_checks?.[booking.id]
                    ),
                    client_requested_call:
                        savedAppointmentCheck.client_requested_call,
                    consult_call_notes: savedAppointmentCheck.consult_call_notes,
                    ...(savedAppointmentCheck.collateral_consent_good_form_on_file !==
                        undefined && {
                        collateral_consent_good_form_on_file:
                            savedAppointmentCheck.collateral_consent_good_form_on_file,
                        collateral_consent_missing_attendee_count:
                            savedAppointmentCheck.collateral_consent_missing_attendee_count ??
                            0,
                        collateral_consent_rechecked: safeToDate(
                            savedAppointmentCheck.collateral_consent_rechecked
                        ),
                    }),
                },
            };
            updateClient(
                bookingField,
                client[bookingField].map((entry) =>
                    entry.id === booking.id ? updatedBooking : entry
                )
            );
            updateClient("appointment_checks", updatedAppointmentChecks);
            if (requestConsultCall && viewFrom === "appt_held" && clinician?.[0]) {
                sendAppointmentHeldEmails(
                    {
                        ...client,
                        appointment_checks: updatedAppointmentChecks,
                    },
                    clinician[0],
                    updatedBooking,
                    requestConsultCall,
                    emailSignature
                );
            }
            addToast("Booking details updated", {
                appearance: "success",
            });
            setShowEditModal(false);
            return true;
        } else {
            addToast("Unable to update booking details", { appearance: "error" });
            return false;
        }
    };

    const handleSave = async () => {
        if (!showEditModal) {
            return;
        }
        if (serviceDeliveryOption === "in_person") {
            if (matchingOfficeWindows.length === 0) {
                addToast(
                    "This booking's time slot has no office window — please select Telemed",
                    { appearance: "error" }
                );
                return;
            }
            if (!location || !office) {
                addToast(
                    "Please select an office location for the in-person appointment",
                    { appearance: "error" }
                );
                return;
            }
        }
        await changeBookingDetails();
    };

    const fetchClinicianServiceSelection = async () => {
        setClinicianServiceSelections(
            await store.fetchClinicianServiceSelection(
                booking?.clinician_id as string
            )
        );
    };

    React.useEffect(() => {
        fetchClinicianServiceSelection();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [booking?.clinician_id]);

    // Current in-person selection encoded as "location||office"
    const currentInPersonValue =
        location && office ? `${location}||${office}` : null;

    return (
        <>
            <Dialog
                open={showEditModal}
                overlayClassName={styles.lightOverlay}
                onClose={() => setShowEditModal(false)}
                title={`Make changes to ${client.first_name}'s booking`}
                saveText="Update"
                onSave={handleSave}
                cancelText="Cancel"
            >
                <div className={styles.changeBookingDetails}>
                    {clinicianServiceSelections[booking.clinician_id]?.departments
                        ?.length > 0 && (
                        <Alert type="info" icon={<InfoIcon />}>
                            <div className={styles.practicesInfo}>
                                <p>
                                    This clinician belongs to the following practice
                                </p>
                                <div className={styles.badgesWrapper}>
                                    {clinicianServiceSelections[
                                        booking.clinician_id
                                    ]?.departments?.map((dep, idx) => (
                                        <Badge
                                            key={idx}
                                            content={dep?.replaceAll("_", " ")}
                                            textColor={
                                                getV2BadgePropsByPractice(dep)
                                                    .textColor
                                            }
                                            bgColor={
                                                getV2BadgePropsByPractice(dep)
                                                    .bgColor
                                            }
                                        />
                                    ))}
                                </div>
                            </div>
                        </Alert>
                    )}
                    <form className="v2-form">
                        <div className="gridTwo">
                            <DatePicker
                                label="Starting Date"
                                selected={startingDate}
                                onChange={(date) => {
                                    setStartingDate(date as Date);
                                }}
                                includeDates={availableApptStartingDates.map(
                                    (date) => date.toDate()
                                )}
                                minDate={new Date()}
                                maxDate={moment().add(4, "weeks").toDate()}
                            />
                            <FormCheckbox
                                label="Request a consult call"
                                checked={requestConsultCall}
                                onChange={() => {
                                    setRequestConsultCall(!requestConsultCall);
                                }}
                                inForm
                            />
                        </div>
                        <div className={styles.serviceDeliveryOption}>
                            <Select
                                options={serviceDeliveryOptions}
                                value={serviceDeliveryOptions.find(
                                    (option) =>
                                        option.value === serviceDeliveryOption
                                )}
                                onChange={(value) => {
                                    const newOption = (value as Option)
                                        .value as ServiceDeliveryOption;
                                    setServiceDeliveryOption(newOption);
                                    if (newOption === "remote") {
                                        setLocation("Remote");
                                        setOffice("");
                                    } else {
                                        if (inPersonOfficeOptions.length === 1) {
                                            const [loc, off] =
                                                inPersonOfficeOptions[0].value.split(
                                                    "||"
                                                );
                                            setLocation(loc);
                                            setOffice(off);
                                        } else {
                                            setLocation("");
                                            setOffice("");
                                        }
                                    }
                                }}
                                label="In-Person or Telemed"
                            />
                        </div>

                        {serviceDeliveryOption === "in_person" &&
                            (matchingOfficeWindows.length === 0 ? (
                                <Alert type="warning" icon={<InfoIcon />}>
                                    This booking's time slot has no approved office
                                    window. Please select <strong>Telemed</strong>{" "}
                                    above.
                                </Alert>
                            ) : (
                                <Select
                                    label="Office location"
                                    options={inPersonOfficeOptions}
                                    value={
                                        inPersonOfficeOptions.find(
                                            (o) => o.value === currentInPersonValue
                                        ) || null
                                    }
                                    onChange={(value) => {
                                        const [loc, off] = (
                                            value as Option
                                        ).value.split("||");
                                        setLocation(loc);
                                        setOffice(off);
                                    }}
                                    isRequired
                                />
                            ))}

                        {canSeeForm && (
                            <Select
                                label="Clinician Practice"
                                options={servicePracticeOptions}
                                value={servicePracticeOptions.find(
                                    (option) => option.value === selectedPractice
                                )}
                                onChange={(value) => {
                                    const selectedDepartment = (value as Option)
                                        .value;
                                    setSelectedPractice(selectedDepartment);
                                    setPracticeId(
                                        allDepartmentsAndServices[selectedDepartment]
                                            ?.practice_id || ""
                                    );
                                }}
                                isRequired
                            />
                        )}

                        {departmentCount > 1 &&
                            !emails.includes(loggedInUserEmail as string) && (
                                <Select
                                    label="Clinician Practice"
                                    options={clinicianServiceOptions}
                                    value={clinicianServiceOptions.find(
                                        (option) => option.value === selectedPractice
                                    )}
                                    onChange={(value) => {
                                        const selectedDepartment = (value as Option)
                                            .value;
                                        setSelectedPractice(selectedDepartment);
                                        setPracticeId(
                                            allDepartmentsAndServices[
                                                selectedDepartment
                                            ]?.practice_id || ""
                                        );
                                    }}
                                    isRequired
                                />
                            )}
                        <Select
                            label="Requested Service"
                            options={requestedServiceOptions}
                            value={requestedServiceOptions.find(
                                (option) =>
                                    option.value
                                        .toLocaleLowerCase()
                                        .replaceAll("_", " ") ===
                                    requestedService
                                        .toLocaleLowerCase()
                                        .replaceAll("_", " ")
                            )}
                            onChange={(value) => {
                                setRequestedService((value as Option).value);
                            }}
                            isLongListInDialog
                        />

                        <TextArea
                            label="Client concerns (for consult call)"
                            value={consultCallNotes}
                            onChange={(e) => setConsultCallNotes(e.target.value)}
                        />
                    </form>
                </div>
            </Dialog>
        </>
    );
}
